import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { AuthProvider } from '@ais-ui/auth';
import { ToasterWithViewState } from '@ais-ui/ui-core';
import { HashRouter as Router } from 'react-router-dom';
import App from './app/app';

import {
  LocalizationProvider,
  useLocalization,
} from '@ais-ui/locale-management';
import { translations } from './locales/translations';
import { ConfigContextProvider } from './configuration/configContext';
import { ErrorBoundary } from '@ais-ui/error-boundaries';
import { ProfileProvider } from '@ais-ui/profile';
import { useFavicon } from './configuration/useFavicon';
import { useSentry } from './configuration/useSentry';
import { useApplicationTitle } from './configuration/useApplicationTitle';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useTheme } from './configuration/useTheme';

class BuggyComponent extends React.Component {
  componentDidMount() {
    // Simulate a JS error
    throw new Error('I crashed!');
  }

  render() {
    // This will not be rendered
    return <h1>Hello World</h1>;
  }
}

function RootComponent() {
  return (
    <StrictMode>
      <ConfigContextProvider>
        <LocalizationProvider translations={translations}>
          <AppComponent />
        </LocalizationProvider>
      </ConfigContextProvider>
    </StrictMode>
  );
}

const AppComponent = () => {
  const { messages } = useLocalization();
  useSentry();
  const { isLoading: isLoadingFavIcon } = useFavicon();
  const { isLoading: isLoadingTitle } = useApplicationTitle();
  const { isLoading: isLoadingTheme } = useTheme();

  if (isLoadingFavIcon || isLoadingTitle || isLoadingTheme) {
    return null;
  }

  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <ProfileProvider>
          <AuthProvider>
            <ToasterWithViewState>
              <ErrorBoundary messages={messages}>
                <App />
              </ErrorBoundary>
            </ToasterWithViewState>
          </AuthProvider>
        </ProfileProvider>
        {/* Only include ReactQueryDevtools in development */}
        {/*{import.meta.env.MODE === 'development' && (*/}
        {/*  <ReactQueryDevtools initialIsOpen={false} position="top-right" />*/}
        {/*)}*/}
      </QueryClientProvider>
    </Router>
  );
};

const queryClient = new QueryClient();
const rootElement = document.getElementById('root') as HTMLElement;
rootElement.className = 'isolate';
let root;

if (!root) {
  root = ReactDOM.createRoot(rootElement);
}

root.render(<RootComponent />);
